import React, { useState, useEffect } from 'react';
import styles from './jiraSettings.module.css';
import { Form, Row, Col, Button, Select, Switch, message } from 'antd';
import { get, post } from '../../utils/fetch';

type ColumnType = { value: string | null; name: string };
interface Props {
  projectId: string;
  organizationId: string;
  integration:
    | {
        enabled: boolean;
        id: string;
        settings: {
          site_id: { value: string; label: string };
          project: { value: string; label: string };
          issue_type: { value: string; label: string };
          new_tasks: boolean;
          new_comments: boolean;
          sync_jira_comments: boolean;
          sync_jira_task_completion: boolean;
          done_column: string;
          task_status: { value: string; label: string };
        };
      }
    | undefined;
}

const JiraSettings: React.FC<Props> = (props: Props) => {
  const [form] = Form.useForm();

  const { integration, organizationId, projectId } = props;

  const [projectIntegrationId, setProjectIntegrationId] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState(
    integration?.settings?.site_id
  );
  const [selectedProject, setSelectedProject] = useState(
    integration?.settings?.project
  );
  const [selectedIssueType, setSelectedIssueType] = useState(
    integration?.settings?.issue_type
  );
  const [selectedStatus, setSelectedStatus] = useState<ColumnType | undefined>(
    integration?.settings?.task_status as ColumnType | undefined
  );
  const [autoPost, setAutoPost] = useState(
    integration?.settings?.new_tasks ?? false
  );
  const [newComments, setNewComments] = useState(
    integration?.settings?.new_comments ?? false
  );
  const [syncCommentsToJira, setSyncCommentsToJira] = useState(
    integration?.settings?.sync_jira_comments ?? false
  );
  const [syncTaskCompletion, setSyncTaskCompletion] = useState(
    integration?.settings?.sync_jira_task_completion ?? false
  );
  const [doneColumn, setDoneColumn] = useState(
    integration?.settings?.done_column
  );

  const [remoteSiteIdOptions, setRemoteSiteIdOptions] = useState([]);
  const [remoteProjectOptions, setRemoteProjectOptions] = useState([]);
  const [remoteIssueTypeOptions, setRemoteIssueTypeOptions] = useState([]);
  const [remoteStatusOptions, setRemoteStatusOptions] = useState<ColumnType[]>(
    []
  );

  const [isSiteIdLoading, setIsSiteIdLoading] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isIssueTypesLoading, setIsIssueTypesLoading] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState(false);

  const getSiteIds = async () => {
    if (!integration?.enabled) return;
    setIsSiteIdLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_site_ids?id=${integration.id}`
      );
      setRemoteSiteIdOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira Site IDs. Please try again.');
    } finally {
      setIsSiteIdLoading(false);
    }
  };

  const getProjects = async () => {
    if (!integration?.enabled) return;
    setIsProjectsLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/remote_projects?id=${integration.id}&site_name=${selectedSiteId.value}`
      );
      setRemoteProjectOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira projects. Please try again.');
    } finally {
      setIsProjectsLoading(false);
    }
  };


  const getIssueTypes = async currentSelectedProject => {
    if (!integration?.enabled || !currentSelectedProject?.value) return;
    setIsIssueTypesLoading(true);
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_issue_types?id=${integration.id}&project_id=${currentSelectedProject.value}&site_id=${selectedSiteId.value}`
      );
      setRemoteIssueTypeOptions(results);
    } catch (error) {
      message.error('Failed to fetch Jira issue types. Please try again.');
    } finally {
      setIsIssueTypesLoading(false);
    }
  };

  const getStatusColumns = async (currentProjectId: string) => {
    if (!integration?.enabled) return;
    setIsStatusLoading(true);
    try {
      const results = await get(`/projects/full?id=${currentProjectId}`);
      const columns: ColumnType[] = [
        { value: null, label: 'None' },
        ...results.columns.map(({ id, name }) => ({
          value: String(id),
          label: name
        }))
      ];
      setRemoteStatusOptions(columns);
    } catch (error) {
      message.error('Failed to fetch status columns. Please try again.');
    } finally {
      setIsStatusLoading(false);
    }
  };

  const getConnectedState = async () => {
    if (!integration?.enabled) return;
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/integration_connection?project_id=${projectId}&integration_code=Jira`
      );
      setProjectIntegrationId(results.project_integration_id);
    } catch (error) {
      message.error('Failed to get integration connection state.');
    }
  };

  const handleProjectSelect = (
    _value: string,
    option: { value: string; label: string }
  ) => {
    setSelectedProject(option);
    setSelectedIssueType(undefined);
    form.setFieldsValue({ issue_type: undefined });
    getIssueTypes(option);
  };

  const handleSiteIdSelect = (
    _value: string,
    option: { value: string; label: string }
  ) => {
    setSelectedSiteId(option);
    getProjects();
    getIssueTypes();
  };


  const handleIssueTypeSelect = (
    _value: string,
    option: { value: string; label: string }
  ) => {
    setSelectedIssueType(option);
  };

  const saveSettings = async () => {
    try {
      await form.validateFields();
      await post(
        `/organizations/${organizationId}/integrations/${integration?.id}/project_integrations/save_settings?integration_code=Jira`,
        {
          project_id: projectId,
          project_integration_id: projectIntegrationId,
          settings: {
            site_id: selectedSiteId,
            project: selectedProject,
            issue_type: selectedIssueType,
            new_tasks: autoPost,
            new_comments: newComments,
            sync_jira_comments: syncCommentsToJira,
            sync_jira_task_completion: syncTaskCompletion,
            done_column: doneColumn,
            task_status: selectedStatus
          }
        }
      );
      message.success('Settings saved successfully.', 5);
    } catch (error) {
      console.error('Error saving settings', error);

      // @ts-expect-error
      if (error?.errorFields) {
        message.error('Please fill in all required fields.');
      } else {
        message.error('Something went wrong while saving. Please try again.');
      }
    }
  };

  const handleDisconnect = async () => {
    try {
      await post(
        `/organizations/${organizationId}/integrations/${integration?.id}/project_integrations/disconnect`,
        { id: projectIntegrationId }
      );
      message.success('Integration disconnected successfully.', 5);
    } catch (error) {
      console.error('Error disconnecting integration', error);
      message.error('Failed to disconnect. Please try again.');
    }
  };

  useEffect(() => {
    if (integration?.enabled) {
      getConnectedState();
      getProjects();
      getSiteIds();
      getStatusColumns(projectId);
    }
  }, [integration, projectId, organizationId]);

  useEffect(() => {
    if (selectedProject?.value) {
      getIssueTypes(selectedProject);
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedSiteId?.value) {
      getProjects();
    }
  }, [selectedSiteId]);

  const renderConnect = () => (
    <div className={styles.jiraExplanation}>
      <p>
        Connect BugHerd with your Jira account to sync your tasks and comments.
      </p>
      <ul>
        <li>Post tasks to your Jira kanban board</li>
        <li>Sync new status updates to your connected tasks</li>
        <li>Sync comments with Jira</li>
      </ul>
      <a
        className="btn ant-btn-primary"
        rel="nofollow"
        data-method="post"
        href={`/auth/atlassian_oauth2?project=${projectId}&organization=${organizationId}&type=Jira`}
      >
        <span className={styles.buttonText}>Connect</span>
      </a>
    </div>
  );

  const renderIntegrationSettings = () => (
    <div>
      <Form
        form={form}
        layout="horizontal"
        onFinish={saveSettings}
        autoComplete="off"
        requiredMark={false}
      >
        <div className={styles.jiraRequired}>
          <div className={styles.requiredBubble}>REQUIRED</div>
          <h3>Connect your Jira project</h3>
          <Row className={styles.jiraEditRow}>
            <Col span={24}>
              <Form.Item
                name="site_id"
                label={
                  <span className={styles.requiredLabel}>
                    <span>*</span>Jira Domain
                  </span>
                }
                className={styles.jiraSiteId}
                rules={[
                  { required: true, message: 'Please select a Jira domain.' }
                ]}
                initialValue={selectedProject?.value}
              >
                <Select
                  className={styles.jiraSelect}
                  showSearch
                  placeholder="Select a domain"
                  optionFilterProp="label"
                  options={remoteSiteIdOptions}
                  onSelect={handleSiteIdSelect}
                  loading={isSiteIdLoading}
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
          {selectedSiteId && (
            <div>
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="project"
                    label={<span className={styles.requiredLabel}>
                                      <span>*</span>Jira Project
                                  </span>}
                    className={styles.jiraProject}
                    rules={[
                      { required: true, message: "Please select a Jira project." }
                    ]}
                    initialValue={selectedProject?.value}
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select a project"
                      optionFilterProp="label"
                      options={remoteProjectOptions}
                      onSelect={handleProjectSelect}
                      loading={isProjectsLoading}
                      allowClear />
                  </Form.Item>
                </Col>
              </Row>
              <Row className={styles.jiraEditRow}>
                <Col span={24}>
                  <Form.Item
                    name="issue_type"
                    label={<span className={styles.requiredLabel}>
                                          <span>*</span>Jira Issue Type
                                      </span>}
                    className={styles.jiraIssueType}
                    rules={[
                      { required: true, message: "Please select an issue type." }
                    ]}
                    initialValue={selectedIssueType?.value}
                  >
                    <Select
                      className={styles.jiraSelect}
                      showSearch
                      placeholder="Select an issue type"
                      optionFilterProp="label"
                      options={remoteIssueTypeOptions}
                      onSelect={handleIssueTypeSelect}
                      loading={isIssueTypesLoading}
                      disabled={!selectedProject}
                      allowClear />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div className={styles.jiraWorkflows}>
          <h3>Choose your desired workflow</h3>
          <Row className={styles.jiraEditRow}>
            <Form.Item
              label="Automatically post new tasks to Jira"
              className={styles.autoPost}
            >
              <Switch checked={autoPost} onChange={e => setAutoPost(e)} />
            </Form.Item>
            {autoPost && (
              <Form.Item
                name="statusColumn"
                label="Create task when status is equal to"
                className={styles.selectStatus}
                initialValue={selectedStatus?.value}
                tooltip="This is an optional field. Select a status to trigger task creation in Jira."
              >
                <Select
                  placeholder="Select a status"
                  options={remoteStatusOptions}
                  onSelect={(value, option) => setSelectedStatus(option)}
                  loading={isStatusLoading}
                  allowClear
                />
              </Form.Item>
            )}
          </Row>
{/*          <Row className={styles.jiraEditRow}>
            <Form.Item
              className={styles.newComments}
              label="Automatically send new BugHerd comments to Jira"
            >
              <Switch checked={newComments} onChange={e => setNewComments(e)} />
            </Form.Item>
          </Row>*/}
          <Row className={styles.jiraEditRow}>
            <Form.Item
              className={styles.syncComments}
              label="Sync comments from Jira to BugHerd"
            >
              <Switch
                checked={syncCommentsToJira}
                onChange={e => setSyncCommentsToJira(e)}
              />
            </Form.Item>
          </Row>
          <Row className={styles.jiraEditRow}>
            <Form.Item
              label="Update BugHerd status when completed in Jira"
              name="doneColumn"
              className={styles.doneColumn}
            >
              <Switch
                checked={syncTaskCompletion}
                onChange={e => setSyncTaskCompletion(e)}
              />
            </Form.Item>
            {syncTaskCompletion && (
              <Form.Item
                className={styles.completedBugherdColumn}
                label="Completed column in BugHerd"
              >
                <Select
                  options={remoteStatusOptions}
                  defaultValue={doneColumn}
                  onSelect={e => setDoneColumn(e)}
                  loading={isStatusLoading}
                  placeholder="Select a column"
                />
              </Form.Item>
            )}
          </Row>
        </div>

        <Row className={styles.jiraEditRow}>
          <Col span={6}>
            <Form.Item>
              <Button
                type="primary"
                shape="round"
                htmlType="submit"
                className={styles.save}
              >
                Save
              </Button>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Button
              type="text"
              shape="round"
              onClick={handleDisconnect}
              className={styles.disconnectButton}
              danger
            >
              Disconnect
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );

  return (
    <div className={styles.container}>
      {integration?.enabled ? renderIntegrationSettings() : renderConnect()}
    </div>
  );
};

export default (props: Props) => <JiraSettings {...props} />;
